/**
 * services/referralsApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Referral program methods — stats, commissions, and withdrawals.
 *
 * METHODS:
 *   getReferralStats(token)               — referral code, link, total earnings
 *   getReferredUsers(token)               — users who signed up via this link
 *   getReferralCommissions(token)         — commission history
 *   getReferralWithdrawals(token)         — payout request history
 *   requestReferralWithdrawal(token,body) — initiate M-Pesa B2C payout
 *   adminGetReferralsOverview(token)      — admin: all referrals summary
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
// ==========================================================================
// ✅ REFERRAL SYSTEM
// All endpoints require an authenticated token.
// Verified users only — backend returns 403 if not verified.
// ==========================================================================

/**
 * GET /api/referrals/
 * Returns the referrer's stats card data + unique referral link.
 * Auto-creates a Referral profile on first call for verified users.
 * Response: {
 *   referral_code, referral_link,
 *   total_referrals, active_referrals,
 *   total_earnings, pending_balance, withdrawn_total
 * }
 */
async getReferralStats(token) {
  try {
    const t = token || this._getToken();
    const res = await fetch(`${API_BASE_URL}/referrals/`, {
      headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.error || `HTTP ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error('❌ getReferralStats error:', error);
    throw error;
  }
},

/**
 * GET /api/referrals/referred-users/
 * Returns paginated list of users who signed up via this referrer's link.
 * Response: { count, results: [ ReferredUser... ] }
 * Each ReferredUser: { id, name, email, initials, premium_type, is_active,
 *                      joined_at, commission_amount, commission_status }
 */
async getReferredUsers(token) {
  try {
    const t = token || this._getToken();
    const res = await fetch(`${API_BASE_URL}/referrals/referred-users/`, {
      headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.error || `HTTP ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error('❌ getReferredUsers error:', error);
    throw error;
  }
},

/**
 * GET /api/referrals/commissions/
 * Returns paginated commission history for the current referrer.
 * Response: { count, results: [ ReferralCommission... ] }
 * Each commission: { id, referred_user_name, referred_user_email,
 *   referred_user_initials, premium_type, date_joined,
 *   transaction_amount, commission_rate, commission_amount,
 *   status, created_at }
 */
async getReferralCommissions(token) {
  try {
    const t = token || this._getToken();
    const res = await fetch(`${API_BASE_URL}/referrals/commissions/`, {
      headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.error || `HTTP ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error('❌ getReferralCommissions error:', error);
    throw error;
  }
},

/**
 * GET /api/referrals/withdrawals/
 * Returns payout history for the current referrer.
 * Response: { count, results: [ ReferralWithdrawal... ] }
 * Each withdrawal: { id, amount, phone, status, mpesa_receipt,
 *                    result_desc, initiated_at, completed_at }
 */
async getReferralWithdrawals(token) {
  try {
    const t = token || this._getToken();
    const res = await fetch(`${API_BASE_URL}/referrals/withdrawals/`, {
      headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.error || `HTTP ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error('❌ getReferralWithdrawals error:', error);
    throw error;
  }
},

/**
 * POST /api/referrals/withdraw/
 * Initiates an M-Pesa B2C payout for the full pending_balance.
 * Body (optional): { phone: '254XXXXXXXXX' }
 *   — if omitted, backend falls back to the user's profile phone_number.
 * Response (202): {
 *   detail, withdrawal_id, amount, phone,
 *   status: 'processing', conversation_id
 * }
 * Throws with err.response.data.detail on backend validation errors.
 */
async requestReferralWithdrawal(token, body = {}) {
  try {
    const t = token || this._getToken();
    const res = await fetch(`${API_BASE_URL}/referrals/withdraw/`, {
      method: 'POST',
      headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.detail || data.error || `HTTP ${res.status}`);
      err.response = { data };
      throw err;
    }
    return data;
  } catch (error) {
    console.error('❌ requestReferralWithdrawal error:', error);
    throw error;
  }
},

/**
 * GET /api/admin/referrals/
 * Admin-only endpoint — returns a summary of all referral profiles.
 * Returns: { count, results: [ { id, referrer, referrer_email,
 *   referral_code, total_referrals, active_referrals,
 *   total_earnings, pending_balance, is_active, created_at } ] }
 */
async adminGetReferralsOverview(token) {
  try {
    const t = token || this._getToken();
    const res = await fetch(`${API_BASE_URL}/admin/referrals/`, {
      headers: { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.error || `HTTP ${res.status}`);
    }
    return await res.json();
  } catch (error) {
    console.error('❌ adminGetReferralsOverview error:', error);
    throw error;
  }
}
};

